import {
  Box, Button, Table, TableContainer, Typography,
} from '@mui/material';

import { DraggablePageConfigCard } from 'containers/DndStackContainer/types';
import { HomePageWidgetType } from 'containers/HomePageContainer/enum/component.enum';
import { HomePageWidgetTypes } from 'containers/HomePageContainer/types/component.type';
import { Loader } from 'common/components/Loader';
import {
  TableWidgetHeader,
} from 'containers/HomePageContainer/components/WidgetList/component/TableWidgetHeader';
import { TableWidgets } from 'containers/HomePageContainer/components/WidgetList/component/TableWidget';
import { Widget } from 'containers/HomePageContainer/types';
import { WidgetType } from 'containers/HomePageContainer/components/WidgetList/component/WidgetType';
import { convertDefaultZoneToUtc } from 'utils/date';
import { getWidgetLayout } from 'containers/HomePageContainer/utils/get-widget-layout';
import { parseSearch } from 'containers/HomePageContainer/utils/parse-search';
import { useHomePageWidgetsStore } from 'containers/HomePageContainer/store/client/use-home-page-widgets.store';
import { useLocation } from 'react-router-dom';
import { useWidgets } from 'containers/HomePageContainer/components/WidgetList/store/server/use-widgets';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { FC, useCallback, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import shallow from 'zustand/shallow';

interface Props {
  handleClose: () => void;
}

export const WidgetList: FC<Props> = ({ handleClose }) => {
  const { search } = useLocation();
  const { culture, date } = parseSearch(search);
  const [types, setTypes] = useState<HomePageWidgetTypes[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    convertDefaultZoneToUtc(date),
  );
  let homePageWidgets = Object.values(HomePageWidgetType);
  homePageWidgets = homePageWidgets.filter(
    (item) => (item !== HomePageWidgetType.HomePage) && (item !== HomePageWidgetType.ChipWidget),
  );
  if (types.length > 0) {
    homePageWidgets = types;
  }
  const { data: widgetsList, isLoading } = useWidgets(
    { date: selectedDate, culture, types: homePageWidgets },
  );

  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);
  const widgets = useHomePageWidgetsStore((store) => store.widgets, shallow);
  const setWidgets = useHomePageWidgetsStore((store) => store.setWidgets);

  const handleAddSelected = useCallback(() => {
    if (selectedWidget) {
      const newWidget: DraggablePageConfigCard = {
        id: selectedWidget.id,
        widgetId: selectedWidget.widgetId,
        name: selectedWidget.name,
        type: selectedWidget.type,
        activeFrom: selectedWidget.activeFrom,
        activeTo: selectedWidget.activeTo,
        layout: getWidgetLayout(selectedWidget),
        sources: selectedWidget.sources,
      };
      const newWidgets: DraggablePageConfigCard[] = [...widgets, newWidget];
      setWidgets(newWidgets);
      handleClose();
    }
  }, [widgets, selectedWidget, handleClose, setWidgets]);

  const handleSetSelect = (widget: Widget) => {
    if (widget.id === selectedWidget?.id) {
      setSelectedWidget(null);
      return;
    }
    setSelectedWidget(widget);
  };

  const handleGetAll = () => {
    setSelectedDate(undefined);
  };

  return (
    <>
      <Box
        data-test="widget-list-modal"
        pt={5}
        pl={2}
        sx={{ color: (theme) => theme.palette.warning.main }}
      >
        <Typography component="span">Only the widgets active for the selected date are shown, click</Typography>
        {' '}
        <Typography
          data-test="show-all-link"
          onClick={handleGetAll}
          component="span"
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: 'blue',
          }}
        >
          here
        </Typography>
        {' '}
        <Typography component="span">to show all widgets.</Typography>
      </Box>
      <Box
        display="flex"
        textAlign="center"
        sx={{ overflowY: 'scroll', height: '60vh' }}
      >
        <Box width="20vw" textAlign="left" pt={2}>
          <Button
            sx={{ marginLeft: 3.5 }}
            disabled={types.length === 0}
            onClick={() => setTypes([])}
            variant="contained"
            data-test="unselect-all"
          >
            unselect all
          </Button>
          <WidgetType types={types} setTypes={setTypes} />
        </Box>
        <Loader isLoading={isLoading} />
        <TableContainer>
          <Table aria-label="simple widgets table">
            <TableWidgetHeader />
            <TableWidgets
              items={widgetsList ?? []}
              handleSelect={handleSetSelect}
              selectedWidget={selectedWidget}
            />
          </Table>
        </TableContainer>
      </Box>
      <Box display="flex" justifyContent="center" pt={2}>
        <Button
          data-test="cancel"
          variant="outlined"
          onClick={handleClose}
          sx={{ mr: 2 }}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          data-test="select-widget"
          variant="contained"
          onClick={handleAddSelected}
          startIcon={<SaveIcon />}
        >
          Select
        </Button>
      </Box>
    </>
  );
};
